import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import { Row } from "./Row";
import { Box, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import { Column } from "./Column";
import { type SxProps } from "@mui/material";

export function FlatAccordion(props: {
  title: string | React.ReactNode;
  subtitle?: string;
  defaultExpanded?: boolean;
  buttonRef?: React.RefObject<{ showForm: () => void }>;
  headerActions?: React.ReactNode;
  expandOnHeaderActionsClick?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
  children: React.ReactNode;
  expandIconPosition?: "left" | "right";
  sx?: SxProps;
}) {
  const {
    title,
    subtitle,
    defaultExpanded,
    buttonRef,
    headerActions,
    expandOnHeaderActionsClick,
    onExpandedChange,
    children,
    expandIconPosition = "right",
  } = props;
  const [isOpen, setIsOpen] = useState<boolean>(!!defaultExpanded);
  return (
    <Accordion
      expanded={isOpen}
      onChange={() => {
        setIsOpen((prev) => {
          const next = !prev;
          onExpandedChange?.(next);
          return next;
        });
      }}
      sx={{
        boxShadow: "none",
        "&.Mui-expanded": {
          margin: 0, // remove extra height when expanded
          minHeight: "40px",
        },
        "&::before": {
          display: "none", // removes the top divider line before the accordion
        },
        "& .MuiAccordionSummary-content.Mui-expanded": {
          margin: "12px 0",
        },
        ...props.sx,
      }}
    >
      <AccordionSummary
        sx={{
          flexDirection: expandIconPosition === "left" ? "row-reverse" : "row",
          alignItems: "center",
          "& .Mui-expanded": { my: 1 },
          px: "28px",
        }}
        expandIcon={<ExpandMoreIcon color="primary" />}
      >
        <Row sx={{ alignItems: "center", width: "100%", pl: 1 }}>
          <Column sx={{ minHeight: "60px", justifyContent: "center" }}>
            {typeof title === "string" ? (
              <Typography variant="body1" fontWeight="bold" fontSize="15px">
                {title}
              </Typography>
            ) : (
              <Typography
                variant="body1"
                fontWeight="bold"
                fontSize="15px"
                sx={{ display: "flex", alignItems: "center" }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary" fontSize="12px">
                {subtitle}
              </Typography>
            )}
          </Column>
          {(headerActions || buttonRef) && (
            <Row sx={{ ml: "auto", alignItems: "center" }}>
              {headerActions && (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    if (expandOnHeaderActionsClick) {
                      setIsOpen(true);
                      onExpandedChange?.(true);
                    }
                  }}
                >
                  {headerActions}
                </Box>
              )}
              {buttonRef && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(true);
                    onExpandedChange?.(true);
                    buttonRef.current?.showForm();
                  }}
                >
                  <AddIcon />
                </IconButton>
              )}
            </Row>
          )}
        </Row>
      </AccordionSummary>
      <AccordionDetails sx={{ pb: "20px", px: 4 }}>{children}</AccordionDetails>
    </Accordion>
  );
}
